import {
  LessonComponentBase,
  LessonDisplayMode
} from '../../content/models/LessonComponent';
import React, { useEffect } from 'react';

import { ApiPortfolioState } from '../../dashboard/models/ApiPortfolioState';
import { LessonState } from '../../dashboard/models/LessonPlayerContext';
import { PortfolioAsset } from '../../dashboard/models/PortfolioAsset';
import { useScrollPosition } from '../../dashboard/hooks/useScrollPosition';

/** Delay, in milliseconds, before the new scroll should be calculated. */
const SCROLL_DEBOUNCE_ms = 500;

/**
 * Hook to track the scroll position of the lesson container, to be stored and
 * applied the next time the portfolio is opened.
 * @param mode Display mode.
 * @param scrollRef Reference to scrolling element.
 * @param components List of components within the lesson, used to trigger the
 * initial scroll update.
 * @param lessonState State of the lesson portfolio.
 * @param finalized Value indicating whether the lesson view is finalized.
 * @param assets Portfolio asset values. Currently it is {position: number}
 * @param portfolioLocked Portfolio status for old versions. No need to save scroll position
 */
const useLessonProgress = (
  mode: LessonDisplayMode,
  scrollRef: React.MutableRefObject<HTMLDivElement>,
  components: LessonComponentBase[],
  lessonState: LessonState,
  finalized: boolean,
  assets: PortfolioAsset,
  portfolioLocked: boolean
) => {
  const { updateScrollPosition } = useScrollPosition(
    lessonState?.answerParams?.userkey,
    lessonState?.answerParams?.lessonkey,
    lessonState?.answerParams?.layer
  );

  /** Returns an array of DOM elements corresponding to each component. */
  const domComponents = (): HTMLElement[] => {
    const arrayElements = scrollRef.current?.querySelectorAll(
      '[data-componentkey]'
    );
    return arrayElements ? Array.prototype.slice.call(arrayElements) : [];
  };

  useEffect(() => {
    if (
      mode === 'learn' && // scroll position needs to be saved / retrieved only for learner
      assets?.position &&
      components.length &&
      scrollRef.current &&
      !finalized &&
      !portfolioLocked // scroll position needs to be retrieved only for current version attempts.
    ) {
      const componentkey = assets?.position;
      const dom = domComponents();
      const index = dom?.findIndex(
        (element) => +element.getAttribute('data-componentkey') === componentkey
      );
      if (index >= 0) {
        dom[index].scrollIntoView(true);
      }
    }
  }, [assets, components.length, scrollRef.current, finalized]);

  useEffect(() => {
    if (
      scrollRef.current &&
      mode === 'learn' &&
      components.length &&
      !finalized &&
      !portfolioLocked
    ) {
      let timer: NodeJS.Timeout;
      let position = assets?.position;
      const onScroll = () => {
        clearTimeout(timer);
        timer = setTimeout(() => {
          const dom = domComponents();
          const index = dom.findIndex(
            (element) => element.getBoundingClientRect().top >= 0
          );
          if (index >= 0) {
            const componentkey = +dom[index].getAttribute('data-componentkey');
            if (componentkey !== position) {
              updateScrollPosition(componentkey);
              position = componentkey;
            }
          }
        }, SCROLL_DEBOUNCE_ms);
      };
      scrollRef.current?.addEventListener('scroll', onScroll);
      return () => {
        scrollRef.current?.removeEventListener('scroll', onScroll);
        clearTimeout(timer);
      };
    }
  }, [assets, components.length, scrollRef.current, portfolioLocked]);
};

export default useLessonProgress;
